import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { APP_VERSION } from "../constants/appVersion";

ChartJS.register(...registerables);

const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const COLORS = [
  "#FB2C36",
  "#9CA3AF",
  "#10B981",
  "#DFCCF1",
  "#374151",
  "#B45309",
  "#10B981",
];

const EngTable21 = ({ data }) => {
  const pdfRef = useRef();

  // Extract years from data automatically
  const years = ["2018", "2019", "2020", "2021", "2022", "2023"];

  const chartData = {
    labels: years,
    datasets: data.map((item, index) => ({
      label: item.race_label,
      data: years.map((year) => parseInt(item[`year_${year}`])),
      fill: false,
      borderColor: COLORS[index % COLORS.length],
      borderWidth: 3,
      pointBackgroundColor: "#fff",
      pointBorderColor: COLORS[index % COLORS.length],
      pointRadius: 6,
      tension: 0.35,
    })),
  };

  // 📊 Summary Insights
  const latestValues = data.map((item) => ({
    race: item.race_label,
    val: parseInt(item.year_2023),
  }));

  const max = latestValues.reduce((a, b) => (a.val > b.val ? a : b));
  const min = latestValues.reduce((a, b) => (a.val < b.val ? a : b));

  const avg = (
    latestValues.reduce((s, i) => s + i.val, 0) / latestValues.length
  ).toFixed(2);

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "Employment Trends by Race (2018-2023)",
        font: { size: 20, weight: "bold" },
      },
    },
    scales: {
      y: {
        title: { display: true, text: "Employment" },
      },
      x: {
        title: { display: true, text: "Year" },
      },
    },
  };

  // 📄 PDF Export
  const downloadPDF = () => {
    const input = pdfRef.current;
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        const logoWidth = 20; // ideal header width
        const logoHeight = 20; // adjust ratio if needed

        pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY,
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right",
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // Add more pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Final footer
      // ===== FINAL FOOTER SAFE AREA =====

      const footerTopY = pageHeight - 40; // start footer 40mm from bottom
      const footerContentY = pageHeight - 28; // logos row
      const footerBottomTextY = pageHeight - 10; // bottom text row

      // Top Divider Line
      pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

      // UtahData logo
      const utahWidth = 18;
      const utahHeight = 18;
      pdf.addImage(
        utahData,
        "PNG",
        20,
        footerContentY - 8,
        utahWidth,
        utahHeight,
      );

      // Built by
      pdf.setFontSize(10);
      pdf.text("Built by", 45, footerContentY);

      // BODEX logo
      const bodexWidth = 36;
      const bodexHeight = bodexWidth * 0.3;
      pdf.addImage(
        bodex,
        "PNG",
        60,
        footerContentY - 6,
        bodexWidth,
        bodexHeight,
      );

      // Using text
      pdf.text("using", 102, footerContentY);

      // KPI logo
      pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

      // Version
      pdf.text(APP_VERSION, 140, footerContentY);

      // Bottom Info Line
      pdf.setFontSize(8);
      pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
      pdf.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        pageWidth - 10,
        footerBottomTextY,
        null,
        null,
        "right",
      );

      pdf.save("employment_trends_race.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">
          📊 Employment Trends by Race (2018–2023)
        </h2>

        {/* 🔍 Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-green-100 rounded-lg text-center shadow">
            <p className="text-sm text-gray-600">Highest (2023)</p>
            <p className="text-lg font-bold text-green-700">
              {max.race}: {max.val}
            </p>
          </div>

          <div className="p-4 bg-red-100 rounded-lg text-center shadow">
            <p className="text-sm text-gray-600">Lowest (2023)</p>
            <p className="text-lg font-bold text-red-700">
              {min.race}: {min.val}
            </p>
          </div>

          <div className="p-4 bg-blue-100 rounded-lg text-center shadow">
            <p className="text-sm text-gray-600">Average (2023)</p>
            <p className="text-lg font-bold text-blue-700">
              {Number(avg).toLocaleString("en-US")}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* 🔹 Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full text-center shadow-md border rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2">Race</th>
                    {years.map((y) => (
                      <th key={y} className="p-2">
                        {y}
                      </th>
                    ))}
                    <th className="p-2">Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-100 transition">
                      <td className="p-2 font-semibold">{row.race_label}</td>
                      {years.map((year) => (
                        <td key={year} className="p-2">
                          {Number(row[`year_${year}`]).toLocaleString("en-US")}
                        </td>
                      ))}
                      <td className="p-2 text-indigo-700 font-bold">
                        {Number(row.employment_trend).toLocaleString("en-US")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 📈 Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[600px] w-full h-96 p-4">
              <Line data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* 📥 Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
};

export default EngTable21;

